import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, useTheme } from '@mui/material';

import { extractNpsData } from 'src/components/dashboard/DashboardPage/widgets/NpsGaugeWidget/helper';
import useNpsScoreCalculation from 'src/components/dashboard/DashboardPage/widgets/NpsGaugeWidget/hooks/useNpsScoreCalculation';
import WidgetLegend from 'src/components/dashboard/DashboardPage/widgets/SpeedometerWidget/WidgetLegend';
import Gauge from 'src/components/followUp/common/BaseWidgets/Gauge';
import type { AggregateList } from 'src/components/followUp/common/types';

import { styles } from './WidgetViewCommonWidget.styles';

interface WidgetViewNPSGaugeWidgetProps {
  data: AggregateList;
}

const WidgetViewNPSGaugeWidget: FC<WidgetViewNPSGaugeWidgetProps> = ({
  data,
}) => {
  const NPS_GAUGE_WIDGET_TEST_ID = 'nps-gauge-widget';
  const { t } = useTranslation('followUp');
  const { colors } = useTheme();

  const { npsScores: currentData } = useNpsScoreCalculation({ data });
  const rawCurrentData = useMemo(() => extractNpsData(data), [data]);

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={styles.wrapper}
      data-test-id={NPS_GAUGE_WIDGET_TEST_ID}
    >
      <Gauge score={currentData?.totalScore ?? 0} />
      <Stack
        direction="column"
        sx={{
          justifyContent: 'center',
          flexGrow: 1,
        }}
      >
        <WidgetLegend
          labelValuePairs={[
            {
              label: t('npsGroup.promoter'),
              value: `${currentData?.promoters ?? 0}%`,
              colorOverride: colors.tertiary[700],
              key: 'Promoter',
              toolTip: `n=${rawCurrentData.promoters ?? 0}`,
            },
            {
              label: t('npsGroup.passive'),
              value: `${currentData?.passives ?? 0}%`,
              colorOverride: colors.neutral[500],
              key: 'Passive',
              toolTip: `n=${rawCurrentData.passives ?? 0}`,
            },
            {
              label: t('npsGroup.detractor'),
              value: `${currentData?.detractors ?? 0}%`,
              colorOverride: colors.error[600],
              key: 'Detractor',
              toolTip: `n=${rawCurrentData.detractors ?? 0}`,
            },
          ]}
        />
      </Stack>
    </Stack>
  );
};
export default WidgetViewNPSGaugeWidget;
